import React, { useRef, useState, useEffect } from 'react';
import { Download, Upload } from 'lucide-react';
import { MaintenanceForm } from '../forms/MaintenanceForm';
import { useAppContext } from '../../contexts/AppContext';
import { MaintenanceEntry, MaintenanceType, Truck } from '../../types';
import { downloadTextFile, getCsvCell, makeImportId, parseCsvRecords, parseDate, parseNumber, toCsv } from '../../utils/dataExchange';
import { formatDistance, getDistanceUnitLabel, getMileageCsvHeader, readMileageFromCsvRecord, toDisplayDistance, useDistanceUnit } from '../../utils/distance';

const maintenanceTypes: MaintenanceType[] = [
  'Oil Change',
  'Tire Replacement',
  'Brake Inspection',
  'Engine Service',
  'Transmission Service',
  'DOT Inspection',
  'General Repair',
  'Preventive Maintenance',
  'Emergency Repair',
  'Annual Inspection',
  'Safety Check'
];

const normalizeMaintenanceType = (value: string): MaintenanceType => {
  const normalized = value.toLowerCase().replace(/[^a-z0-9]/g, '');
  const aliases: Record<string, MaintenanceType> = {
    oil: 'Oil Change',
    oilchange: 'Oil Change',
    tires: 'Tire Replacement',
    tirereplacement: 'Tire Replacement',
    brakes: 'Brake Inspection',
    brakeinspection: 'Brake Inspection',
    engine: 'Engine Service',
    engineservice: 'Engine Service',
    transmission: 'Transmission Service',
    transmissionservice: 'Transmission Service',
    dot: 'DOT Inspection',
    dotinspection: 'DOT Inspection',
    repair: 'General Repair',
    generalrepair: 'General Repair',
    preventive: 'Preventive Maintenance',
    preventivemaintenance: 'Preventive Maintenance',
    emergency: 'Emergency Repair',
    emergencyrepair: 'Emergency Repair',
    annual: 'Annual Inspection',
    annualinspection: 'Annual Inspection',
    safety: 'Safety Check',
    safetycheck: 'Safety Check'
  };

  return aliases[normalized] || maintenanceTypes.find(type => type.toLowerCase() === value.toLowerCase()) || 'General Repair';
};

const MaintenancePage: React.FC<any> = ({ trucks: propTrucks, maintenanceEntries: propMaintenanceEntries, onAddEntry }) => {
  const { state, addMaintenance, updateMaintenance, deleteMaintenance } = useAppContext();
  const { trucks: stateTrucks, maintenance: stateMaintenance } = state;
  const distanceUnit = useDistanceUnit();
  const importInputRef = useRef<HTMLInputElement>(null);
  const [showMaintenanceForm, setShowMaintenanceForm] = useState(false);
  const [editingMaintenance, setEditingMaintenance] = useState<any>(null);
  const [displayedEntries, setDisplayedEntries] = useState<any[]>([]);
  const [importFeedback, setImportFeedback] = useState<{ tone: 'success' | 'warning' | 'danger'; text: string; details?: string[] } | null>(null);


  const trucks = propTrucks || stateTrucks;
  const maintenanceEntries = propMaintenanceEntries || stateMaintenance;


  useEffect(() => {
    setDisplayedEntries(maintenanceEntries || []);
  }, [maintenanceEntries]);

  const handleAddMaintenanceClick = () => {
    setEditingMaintenance(null);
    setShowMaintenanceForm(true);
  };

  const handleEditMaintenance = (entry: any) => {
    setEditingMaintenance(entry);
    setShowMaintenanceForm(true);
  };

  const handleDeleteMaintenance = async (entry: any) => {
    if (window.confirm(`Are you sure you want to delete this maintenance entry? This action cannot be undone.`)) {
      try {
        await deleteMaintenance(entry.id);
      } catch (error) {
        console.error('Error deleting maintenance entry:', error);
        alert('Failed to delete maintenance entry. Please try again.');
      }
    }
  };

  const handleMaintenanceSaved = async (entry: any) => {
    if (editingMaintenance) {
      await updateMaintenance(editingMaintenance.id, entry);
    } else {
      await addMaintenance(entry);

      if (onAddEntry) {
        onAddEntry(entry);
      }
    }
    
    setShowMaintenanceForm(false);
    setEditingMaintenance(null);
  };

  const handleExportMaintenance = () => {
    const headers = [
      'ID',
      'Truck ID',
      'License Plate',
      'Type',
      'Date',
      getMileageCsvHeader(distanceUnit),
      'Cost',
      'Performed By',
      'Invoice Number',
      'Status',
      'Notes'
    ];
    const rows = displayedEntries.map((entry: MaintenanceEntry) => {
      const truck = trucks?.find((candidate: Truck) => candidate.id === entry.truckId);
      return [
        entry.id,
        entry.truckId,
        truck?.licensePlate || '',
        entry.type,
        new Date(entry.date).toISOString().split('T')[0],
        Math.round(toDisplayDistance(entry.mileage, distanceUnit)),
        entry.cost,
        entry.performedBy || '',
        entry.invoiceNumber || '',
        entry.status || '',
        entry.notes || ''
      ];
    });

    downloadTextFile(
      `maintenance-export-${new Date().toISOString().split('T')[0]}.csv`,
      toCsv(headers, rows),
      'text/csv;charset=utf-8;'
    );
    setImportFeedback({
      tone: 'success',
      text: `Exported ${displayedEntries.length} maintenance record${displayedEntries.length === 1 ? '' : 's'}.`
    });
  };

  const handleImportMaintenanceClick = () => {
    importInputRef.current?.click();
  };

  const handleImportMaintenance = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const { records } = parseCsvRecords(text);
      const knownEntries = [...maintenanceEntries];
      const errors: string[] = [];
      const actor = state.currentUser?.id || 'import';
      const now = new Date();
      let added = 0;
      let updated = 0;

      if (records.length === 0) {
        setImportFeedback({ tone: 'warning', text: 'No maintenance rows were found in that CSV.' });
        return;
      }

      for (let index = 0; index < records.length; index += 1) {
        const record = records[index];
        const rowNumber = index + 2;
        const id = getCsvCell(record, ['ID', 'Maintenance ID', 'Entry ID']);
        const truckIdValue = getCsvCell(record, ['Truck ID', 'Vehicle ID']);
        const licensePlate = getCsvCell(record, ['License Plate', 'Plate', 'Vehicle', 'Truck']);
        const truck = trucks?.find((candidate: Truck) =>
          (truckIdValue && candidate.id === truckIdValue) ||
          (licensePlate && candidate.licensePlate.toLowerCase() === licensePlate.toLowerCase()) ||
          (licensePlate && (candidate.nickname || '').toLowerCase() === licensePlate.toLowerCase())
        );

        if (!truck) {
          errors.push(`Row ${rowNumber}: no matching truck found. Use Truck ID or License Plate from your fleet.`);
          continue;
        }

        const type = normalizeMaintenanceType(getCsvCell(record, ['Type', 'Maintenance Type', 'Service Type']));
        const date = parseDate(getCsvCell(record, ['Date', 'Service Date', 'Performed Date']), now);
        const mileage = readMileageFromCsvRecord(record, distanceUnit);
        const cost = Math.max(0, parseNumber(getCsvCell(record, ['Cost', 'Amount', 'Total']), 0));
        const performedBy = getCsvCell(record, ['Performed By', 'Vendor', 'Mechanic', 'Shop']);
        const invoiceNumber = getCsvCell(record, ['Invoice Number', 'Invoice', 'Receipt']);
        const status = getCsvCell(record, ['Status']);
        const notes = getCsvCell(record, ['Notes', 'Description', 'Details']);

        const existingEntry = knownEntries.find((entry: MaintenanceEntry) => id && entry.id === id);

        if (existingEntry) {
          const updates: Partial<MaintenanceEntry> = {
            truckId: truck.id,
            type,
            date,
            mileage,
            cost,
            performedBy,
            invoiceNumber,
            status,
            notes,
            updatedAt: now,
            updatedBy: actor
          };

          await updateMaintenance(existingEntry.id, updates);
          Object.assign(existingEntry, updates);
          updated += 1;
          continue;
        }

        const newEntry: MaintenanceEntry = {
          id: id || makeImportId('maintenance', rowNumber),
          truckId: truck.id,
          type,
          date,
          mileage,
          cost,
          performedBy,
          invoiceNumber,
          status,
          notes,
          createdAt: now,
          createdBy: actor,
          updatedAt: now,
          updatedBy: actor
        };

        await addMaintenance(newEntry);
        if (onAddEntry) {
          onAddEntry(newEntry);
        }
        knownEntries.unshift(newEntry);
        added += 1;
      }

      const skipped = errors.length;
      setImportFeedback({
        tone: skipped > 0 ? (added + updated > 0 ? 'warning' : 'danger') : 'success',
        text: `Import complete: ${added} added, ${updated} updated, ${skipped} skipped.`,
        details: errors.slice(0, 8)
      });
    } catch (error) {
      setImportFeedback({
        tone: 'danger',
        text: error instanceof Error ? error.message : 'Maintenance import failed. Please check the CSV and try again.'
      });
    } finally {
      event.target.value = '';
    }
  };

  return (
    <div className="maintenance-page">
      <div className="page-header">
        <div>
          <h1>Maintenance Logs</h1>
          <p>Import, export, and manage service records tied to your fleet vehicles.</p>
        </div>
        <div className="page-actions">
          <input
            ref={importInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleImportMaintenance}
            style={{ display: 'none' }}
          />
          <button className="btn btn-secondary" onClick={handleExportMaintenance}>
            <Download size={16} />
            Export CSV
          </button>
          <button className="btn btn-secondary" onClick={handleImportMaintenanceClick}>
            <Upload size={16} />
            Import CSV
          </button>
          <button className="btn btn-primary" onClick={handleAddMaintenanceClick}>+ Log Maintenance</button>
        </div>
      </div>

      {importFeedback && (
        <div className={`import-feedback ${importFeedback.tone}`} role="status">
          <strong>{importFeedback.text}</strong>
          {importFeedback.details && importFeedback.details.length > 0 && (
            <ul>
              {importFeedback.details.map(detail => (
                <li key={detail}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <table className="maintenance-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Truck</th>
                <th>Type</th>
                <th>Mileage ({getDistanceUnitLabel(distanceUnit)})</th>
                <th>Cost</th>
                <th>Performed By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedEntries.length === 0 ? (
                <tr>
                  <td colSpan={7}>No maintenance entries found</td>
                </tr>
              ) : displayedEntries.map((entry: any) => {
                const truck = trucks?.find((t: any) => t.id === entry.truckId);
                return (
                  <tr key={entry.id}>
                    <td>{new Date(entry.date).toLocaleDateString()}</td>
                    <td>{truck?.nickname || truck?.licensePlate || 'Unknown'}</td>
                    <td>{entry.type}</td>
                    <td>{formatDistance(entry.mileage, { unit: distanceUnit, includeUnit: false })}</td>
                    <td>${entry.cost}</td>
                    <td>{entry.performedBy || 'N/A'}</td>
                    <td>
                      <button 
                        className="btn btn-sm btn-secondary" 
                        onClick={() => handleEditMaintenance(entry)}
                        title="Edit maintenance entry"
                      >
                        Edit
                      </button>
                      <button 
                        className="btn btn-sm btn-danger" 
                        onClick={() => handleDeleteMaintenance(entry)} 
                        style={{ marginLeft: '8px' }}
                        title="Delete maintenance entry"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showMaintenanceForm && (
        <MaintenanceForm
          maintenance={editingMaintenance}
          onSuccess={handleMaintenanceSaved}
          onCancel={() => {
            setShowMaintenanceForm(false);
            setEditingMaintenance(null);
          }}
        />
      )}
    </div>
  );
};

export default MaintenancePage;
